<template>
  <div class="results6-wrap">

    <div class="topbar">
      <div class="topbar-inner">
        <div class="brand">
          <div class="brand-mark"></div>
          <div class="brand-text">SEQ <small>Swiss Eating Quiz · Health Professional Report</small></div>
        </div>
        <div class="topbar-meta">CLINICAL REPORT · 09.05.2026 · ID 2841-CH</div>
      </div>
    </div>

    <div class="triage-banner show">
      <div class="triage-banner-inner">
        <span>⚠ 2 patterns flagged for clinical follow-up · see Section 02</span>
        <span>Open dietician referral suggested</span>
      </div>
    </div>

    <div class="hero">
      <div class="hero-inner">
        <div class="hero-eyebrow">Clinical Dietary Summary</div>
        <h1 class="hero-title">Patient's reported eating habits, in <em>clinical context</em>.</h1>
        <p class="hero-sub">
          A scannable summary of the participant's Swiss Eating Quiz, presented for general practitioners and health professionals. Designed to support clinical judgement, referral decisions, and patient conversations; not to replace dietetic assessment.
        </p>
      </div>
    </div>

    <div class="meta-grid">
      <div v-for="cell in metaCells" :key="cell.label" class="meta-cell">
        <div class="meta-label">{{ cell.label }}</div>
        <div class="meta-value">{{ cell.value }}</div>
      </div>
    </div>

    <!-- SECTION 1: TOP-LINE SUMMARY -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">01 / 06</div>
          <div>
            <h2 class="section-title">Top-line <em>summary</em>.</h2>
            <p class="section-lede">
              Read this section in 60 seconds. Score, dietary pattern, and the two or three things that matter clinically.
            </p>
          </div>
        </div>

        <div class="summary-grid">
          <div v-for="tile in summaryTiles" :key="tile.label" class="summary-tile">
            <div class="metric-label">{{ tile.label }}</div>
            <div class="metric-value" :class="tile.status" :style="tile.small ? { fontSize: '18px' } : {}">
              {{ tile.value }}<span v-if="tile.unit" class="unit">{{ tile.unit }}</span>
            </div>
            <div class="metric-detail">{{ tile.detail }}</div>
          </div>
        </div>

        <div class="pattern-card">
          <div class="pattern-icon">MIXED<br>WESTERN</div>
          <div>
            <div class="pattern-text">
              <strong>Dietary pattern: mixed western, with vegetable-positive baseline.</strong>
              <p>
                Adequate vegetables and fruit, supported by water and dairy. Animal-protein contribution is moderate but skewed toward processed meats. Refined grains dominate the carbohydrate side; whole grains underused. Plant proteins (Hülsenfrüchte, legumes) almost absent. Discretionary intake (sweets, alcohol) not measured in v1.
              </p>
            </div>
            <div class="pattern-axes">
              <div v-for="axis in patternAxes" :key="axis.label" class="pattern-axis">
                <div>{{ axis.label }}</div>
                <div class="pattern-axis-value">{{ axis.value }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 2: CLINICAL RISK -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">02 / 06</div>
          <div>
            <h2 class="section-title">Dietary patterns and <em>clinical risk</em>.</h2>
            <p class="section-lede">
              Conditions whose risk profile is shaped by the reported dietary pattern. The risk direction is associative based on dietary guidelines and meta-analytic evidence, not individual prediction. Cross-reference with vital signs and labs in Section 03.
            </p>
          </div>
        </div>

        <ul class="risk-list">
          <li v-for="r in risks" :key="r.name" :class="r.level">
            <div>
              <div class="risk-head">
                <span class="risk-name">{{ r.name }}</span>
                <span class="pill" :class="r.pill">{{ r.label }}</span>
              </div>
              <div class="risk-body">{{ r.body }}</div>
              <div v-if="r.cross" class="risk-cross">Cross-ref: {{ r.cross }}</div>
            </div>
            <div class="risk-side" v-html="r.refs"></div>
          </li>
        </ul>
      </div>
    </section>

    <!-- SECTION 3: VITALS / LABS CROSS-REFERENCE -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">03 / 06</div>
          <div>
            <h2 class="section-title">Vitals and labs <em>cross-reference</em>.</h2>
            <p class="section-lede">
              Fields to confirm or rule in the risk associations above. SEQ does not collect these; record current or most recent values here for the patient's file.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Anthropometry and vitals</div>
          <div class="card-title">Most recent measurements</div>

          <div class="vitals-grid" style="margin-bottom: 16px;">
            <div v-for="v in vitalsFields" :key="v.label" class="vital">
              <div class="vital-label">{{ v.label }}</div>
              <input class="vital-input" type="text" placeholder="—">
              <div class="vital-unit">{{ v.unit }}</div>
            </div>
          </div>

          <div class="card-eyebrow" style="margin-top: 6px;">Relevant labs (most recent)</div>
          <div class="card-title">For risk confirmation</div>

          <div class="vitals-grid">
            <div v-for="l in labFields" :key="l.label" class="vital">
              <div class="vital-label">{{ l.label }}</div>
              <input class="vital-input" type="text" placeholder="—">
              <div class="vital-unit">{{ l.unit }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 4: REFERRALS -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">04 / 06</div>
          <div>
            <h2 class="section-title">Referral <em>pathway</em>.</h2>
            <p class="section-lede">
              Suggested next steps based on the dietary pattern alone. Final referral decisions remain with the treating clinician and depend on the broader clinical picture.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Suggested actions</div>
          <div class="card-title">Selectable referral options</div>
          <p class="card-sub">Mark the ones taken or planned. Italicised buttons denote SEQ-suggested options based on the pattern.</p>

          <div class="referral-grid">
            <div v-for="r in referrals" :key="r.name" class="referral-card">
              <div>
                <div class="referral-name">{{ r.name }}</div>
                <div class="referral-reason">{{ r.reason }}</div>
              </div>
              <button class="referral-action" :class="r.suggested ? 'suggested' : ''">{{ r.action }}</button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 5: PATIENT CONVERSATION -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">05 / 06</div>
          <div>
            <h2 class="section-title">Patient <em>conversation</em>.</h2>
            <p class="section-lede">
              Suggested framings for discussing the SEQ results with the patient. Phrased to motivate without alarming, and to be patient-respectful. Adapt to context.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Opening the conversation</div>
          <div class="card-title">Suggested talking points</div>

          <div v-for="t in talkingPoints" :key="t.eyebrow" class="talking-card">
            <div class="talking-eyebrow">{{ t.eyebrow }}</div>
            <p>{{ t.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 6: CLINICAL NOTES & SIGN-OFF -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">06 / 06</div>
          <div>
            <h2 class="section-title">Clinician <em>notes</em>.</h2>
            <p class="section-lede">
              Free text and sign-off. Note the decision taken (referral, no referral, watch and wait), any patient-stated preferences, and the planned follow-up.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Clinical decision and rationale</div>
          <div class="card-title">Free text</div>
          <textarea class="notes-area" style="min-height: 140px;" placeholder="Decision, rationale, patient preferences, follow-up plan..."></textarea>

          <div class="sig-row">
            <div class="sig-line">Clinician signature and date</div>
            <div class="sig-line">Patient signature and date (if discussed)</div>
          </div>
        </div>

        <p class="placeholder-note">Placeholder content. Final wording and referral logic to be confirmed with the BFH Departement Gesundheit and Schweizerische Gesellschaft für Ernährung (SGE).</p>
      </div>
    </section>

    <div class="footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <div class="brand">
            <div class="brand-mark"></div>
            <div class="brand-text">SEQ <small>Swiss Eating Quiz · Health Professional Report</small></div>
          </div>
          <p>
            The SEQ is a research instrument developed at Berner Fachhochschule for measuring how Swiss residents' eating habits compare to the Schweizer Lebensmittelpyramide. This professional report supports clinical judgement but is not a diagnostic tool. Risk associations are based on dietary-guideline evidence, not individual prediction.
          </p>
        </div>
        <div class="footer-meta">
          Demo report · Mock data<br>
          v0.1 · May 2026<br>
          BFH Departement Gesundheit
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Results6',
  data() {
    return {
      metaCells: [
        { label: 'Patient ID', value: '2841-CH' },
        { label: 'Quiz completed', value: '9 May 2026, 14:23' },
        { label: 'Language · Type', value: 'DE · Self-reported (adult)' },
        { label: 'Reviewing professional', value: '[ to be filled ]' },
      ],
      summaryTiles: [
        { label: 'Overall score', value: '76', unit: '/ 100', status: '', small: false, detail: 'Mid-upper range. Solid foundation, two specific clinical concerns.' },
        { label: 'Pattern', value: 'Mixed western', unit: '', status: 'warn', small: true, detail: 'Skews to processed meat, refined grains, dairy. Adequate produce.' },
        { label: 'Confidence', value: 'High', unit: '', status: 'good', small: true, detail: '67 / 67 answered · 9 min · no rapid-fire pattern detected.' },
        { label: 'Clinical priority', value: 'Moderate', unit: '', status: 'bad', small: true, detail: '2 elevated risks, 3 attention items. Dietician referral indicated.' },
      ],
      patternAxes: [
        { label: 'Plant ↔ Animal', value: 'animal-leaning' },
        { label: 'Whole ↔ Refined', value: 'refined-leaning' },
        { label: 'Variety', value: 'good (5 / 7 groups)' },
        { label: 'Mediterranean similarity', value: 'low (32 %)' },
      ],
      risks: [
        {
          level: 'elevated', pill: 'bad', label: 'Elevated risk',
          name: 'Cardiovascular disease',
          body: 'Processed meat intake elevated (3× / week vs. ≤ 1 reference). Refined grains dominate. Fatty fish intake zero, omega-3 likely below reference. Combination is consistent with elevated CVD risk in observational studies.',
          cross: 'BP, LDL, HDL, triglycerides, family history',
          refs: 'SEQ Q29–36<br>Q45–54',
        },
        {
          level: 'elevated', pill: 'bad', label: 'Elevated risk',
          name: 'Iron-deficiency anaemia (latent)',
          body: 'Coffee intake high (4+ cups daily, often with or near meals) impairs non-heme iron absorption. Plant proteins (alternative iron source) very low. Of clinical relevance if female of reproductive age, vegetarian, or with prior anaemia.',
          cross: 'ferritin, transferrin saturation, MCV, prior anaemia',
          refs: 'SEQ Q37–44<br>Q66',
        },
        {
          level: 'attention', pill: 'warn', label: 'Attention',
          name: 'Type 2 diabetes',
          body: 'Refined grain dominance and discretionary intake (sugar-added cereal, sweetened yoghurt) suggest a glycaemic-leaning pattern. SEQ does not measure total added sugar; quantification needs food diary or 24h recall.',
          cross: 'HbA1c, fasting glucose, BMI, waist circumference',
          refs: 'SEQ Q45–54<br>Q55–64',
        },
        {
          level: 'attention', pill: 'warn', label: 'Attention',
          name: 'Hypertension',
          body: 'Processed meat (3× / week) and hard cheese (3× / week) contribute meaningfully to sodium intake. Total salt intake likely above 5 g / day reference. Vegetables and fruit adequate (offsets via potassium); net direction warrants BP confirmation.',
          cross: 'BP (at least 2 readings), kidney function',
          refs: 'SEQ Q29–36<br>Q55–64',
        },
        {
          level: 'attention', pill: 'warn', label: 'Attention',
          name: 'Cognitive ageing',
          body: 'Zero fatty-fish intake means omega-3 contribution from diet is negligible. Mediterranean-similarity score is low (32 %). Pattern is sub-optimal for long-term cognitive trajectory; impact is gradual, not immediate.',
          cross: 'age, family history of dementia',
          refs: 'SEQ Q31–33',
        },
        {
          level: 'protective', pill: 'good', label: 'Protective',
          name: 'Hydration and renal load',
          body: 'Water intake 2.0 L / day, no sweetened drinks. Hydration sufficient. No protective concerns for renal function from the dietary side.',
          cross: 'creatinine, eGFR if at risk',
          refs: 'SEQ Q65–67',
        },
        {
          level: 'protective', pill: 'good', label: 'Protective',
          name: 'Calcium and bone health',
          body: 'Dairy intake 14× / week. Calcium from dietary sources likely sufficient. Vitamin D status not inferable from SEQ.',
          cross: '25-OH vitamin D if at risk of deficiency',
          refs: 'SEQ Q55–64',
        },
        {
          level: 'neutral', pill: 'neutral', label: 'No flags',
          name: 'Eating-pattern stability',
          body: 'No pattern suggestive of disordered eating in this SEQ submission (no extreme restriction across multiple groups, no monotony pattern). Time to complete and answer distribution are unremarkable.',
          cross: '',
          refs: 'all sections',
        },
      ],
      vitalsFields: [
        { label: 'Height', unit: 'cm' },
        { label: 'Weight', unit: 'kg' },
        { label: 'BMI', unit: 'kg / m²' },
        { label: 'Waist', unit: 'cm' },
        { label: 'Blood pressure', unit: 'mmHg' },
        { label: 'Pulse', unit: 'bpm' },
        { label: 'Age', unit: 'years' },
        { label: 'Sex', unit: 'M / F / other' },
      ],
      labFields: [
        { label: 'HbA1c', unit: '%' },
        { label: 'Glucose, fasting', unit: 'mmol / L' },
        { label: 'LDL', unit: 'mmol / L' },
        { label: 'HDL', unit: 'mmol / L' },
        { label: 'Triglycerides', unit: 'mmol / L' },
        { label: 'Ferritin', unit: 'ng / mL' },
        { label: 'Hb', unit: 'g / dL' },
        { label: '25-OH vit D', unit: 'nmol / L' },
        { label: 'Vit B12', unit: 'pmol / L' },
        { label: 'Creatinine', unit: 'µmol / L' },
        { label: 'eGFR', unit: 'mL / min / 1.73 m²' },
        { label: 'CRP', unit: 'mg / L' },
      ],
      referrals: [
        { name: 'Dietician (ambulatory)', reason: 'For Hülsenfrüchte introduction, whole-grain swap coaching, fish-meal planning. Insurance-covered with GP referral.', suggested: true, action: 'Suggested' },
        { name: 'SGE materials handout', reason: 'Lebensmittelpyramide, Tellermodell. Suitable as self-management starter.', suggested: true, action: 'Suggested' },
        { name: 'Endocrinology / diabetes service', reason: 'If HbA1c or fasting glucose elevated, given the glycaemic-leaning pattern.', suggested: false, action: 'If indicated' },
        { name: 'Cardiology / lipid clinic', reason: 'If lipids or BP confirm elevated CVD risk.', suggested: false, action: 'If indicated' },
        { name: 'Anaemia work-up', reason: 'Ferritin, transferrin, MCV. Indicated if female reproductive age or symptomatic.', suggested: false, action: 'If indicated' },
        { name: 'Self-management plan (no referral)', reason: 'Patient declines referral or prefers self-directed change.', suggested: false, action: 'Option' },
        { name: 'Re-take SEQ in 3 months', reason: 'To track change after intervention. Free, low-friction tool.', suggested: true, action: 'Suggested' },
        { name: 'Public health programme', reason: 'E.g. workplace wellness, community nutrition group. Locality-dependent.', suggested: false, action: 'Option' },
      ],
      talkingPoints: [
        { eyebrow: 'Affirm the positives first', body: "Looking at this, the good news is your everyday foundations are solid. You drink plenty of water, you eat your vegetables and fruit, and you avoid sugary drinks. That's already a strong base, and it's not where most patients I see are." },
        { eyebrow: 'Name the specific gap', body: "The thing that stands out is plant proteins, things like lentils, chickpeas, or tofu. They barely appear in your week. That's not a problem on its own, but it would help me understand if it's a preference, a habit, or something you'd be open to exploring." },
        { eyebrow: 'Frame the referral', body: "I'd like to send you to a dietician for one or two sessions. Not because anything is wrong, but because they can help you find practical ways to introduce these foods without changing your whole routine. Insurance covers this with my referral." },
        { eyebrow: 'Address coffee gently', body: "You mentioned 4 cups of coffee a day. That's fine in itself, but coffee with meals can interfere with how your body absorbs iron from food. If we ever check your iron levels, we can look at this together." },
        { eyebrow: 'Re-take and track', body: 'The SEQ takes 10 minutes. If you want to do it again in three months, we can see how things have moved. No pressure, no judgement, just a quick check-in with yourself.' },
      ],
    };
  },
};
</script>

<style scoped>
.results6-wrap {
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --ink-mute: #6B6B6B;
  --ink-dim: #9C9C9C;
  --rule: #DDDDDD;
  --rule-strong: #1A1A1A;
  --paper: #FAFAF7;
  --card: #FFFFFF;
  --warm: #F2EDE4;
  --good: #2D8659;
  --good-bg: #B8D8C5;
  --warn: #E8A33D;
  --warn-bg: #F5D9A8;
  --bad: #C8392E;
  --bad-bg: #ECB8B3;
  --neutral: #4A6FA5;
  --neutral-bg: #C8D4E6;
  --accent-rule: #DA291C;
  --mono: 'JetBrains Mono', 'SF Mono', Consolas, monospace;

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: var(--ink);
  background: var(--paper);
  min-height: 100%;
}
.results6-wrap *,
.results6-wrap *::before,
.results6-wrap *::after {
  box-sizing: border-box;
}

/* TOPBAR */
.topbar {
  background: var(--ink);
  color: white;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  padding: 12px 32px;
}
.topbar-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1100px;
  margin: 0 auto;
}
.brand { display: flex; align-items: center; gap: 12px; }
.brand-mark { width: 18px; height: 18px; background: var(--accent-rule); }
.brand-text {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.02em;
}
.brand-text small {
  display: block;
  font-weight: 400;
  font-size: 10px;
  letter-spacing: 0.04em;
  color: var(--ink-dim);
  margin-top: -2px;
}
.topbar-meta { font-size: 11px; }

/* HEADER */
.hero {
  background: var(--card);
  padding: 32px;
  border-bottom: 1px solid var(--rule);
}
.hero-inner { max-width: 1100px; margin: 0 auto; }
.hero-eyebrow {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--bad);
  margin-bottom: 12px;
}
.hero-title {
  font-size: 36px;
  font-weight: 600;
  line-height: 1.15;
  margin: 0 0 12px 0;
  letter-spacing: -0.01em;
}
.hero-title em { font-style: italic; color: var(--bad); }
.hero-sub {
  color: var(--ink-mute);
  font-size: 15px;
  max-width: 680px;
  margin: 0 0 24px 0;
}

/* META GRID */
.meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  background: var(--warm);
}
.meta-cell {
  padding: 14px 20px;
  border-right: 1px solid var(--rule);
  font-size: 12px;
}
.meta-cell:last-child { border-right: none; }
.meta-label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.meta-value { font-weight: 500; color: var(--ink); }

/* TRIAGE BANNER */
.triage-banner {
  background: var(--bad);
  color: white;
  padding: 14px 32px;
  font-family: var(--mono);
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  display: none;
}
.triage-banner.show { display: block; }
.triage-banner-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* SECTION */
.section {
  padding: 36px 32px;
  border-bottom: 1px solid var(--rule);
}
.section.warm { background: var(--warm); }
.section-inner { max-width: 1100px; margin: 0 auto; }
.section-header {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 24px;
  margin-bottom: 24px;
}
.section-num {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  color: var(--ink-dim);
  padding-top: 8px;
  border-top: 1px solid var(--ink);
}
.section-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  line-height: 1.2;
}
.section-title em { font-style: italic; color: var(--bad); }
.section-lede {
  color: var(--ink-mute);
  font-size: 14px;
  max-width: 780px;
}

/* CARD */
.card {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 22px;
  margin-bottom: 14px;
}
.card-eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-dim);
  margin-bottom: 4px;
}
.card-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}
.card-sub {
  color: var(--ink-mute);
  font-size: 13px;
  margin-bottom: 18px;
}

/* PILLS */
.pill {
  display: inline-block;
  font-family: var(--mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 2px;
  white-space: nowrap;
}
.pill.good { background: var(--good-bg); color: var(--good); }
.pill.warn { background: var(--warn-bg); color: var(--warn); }
.pill.bad { background: var(--bad-bg); color: var(--bad); }
.pill.flat { background: var(--rule); color: var(--ink-mute); }
.pill.neutral { background: var(--neutral-bg); color: var(--neutral); }

/* TOP SUMMARY GRID (4-up) */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 16px;
}
.summary-tile {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 14px 16px;
}
.summary-tile .metric-label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.summary-tile .metric-value {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.1;
}
.summary-tile .metric-value .unit {
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-mute);
  margin-left: 4px;
}
.summary-tile .metric-value.good { color: var(--good); }
.summary-tile .metric-value.warn { color: var(--warn); }
.summary-tile .metric-value.bad { color: var(--bad); }
.summary-tile .metric-detail {
  font-size: 11px;
  color: var(--ink-mute);
  margin-top: 4px;
  line-height: 1.4;
}

/* DIETARY PATTERN */
.pattern-card {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 22px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
}
.pattern-icon {
  width: 80px;
  height: 80px;
  border: 2px solid var(--bad);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--mono);
  font-size: 14px;
  font-weight: 600;
  color: var(--bad);
  text-align: center;
  letter-spacing: 0.04em;
}
.pattern-text strong { font-size: 16px; }
.pattern-text p { margin: 4px 0 0 0; color: var(--ink-mute); font-size: 13px; }
.pattern-axes {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 14px;
}
.pattern-axis {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-mute);
}
.pattern-axis-value {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  margin-top: 2px;
}

/* RISK LIST */
.risk-list { list-style: none; margin: 0; padding: 0; }
.risk-list li {
  background: var(--card);
  border: 1px solid var(--rule);
  border-left: 4px solid var(--rule);
  padding: 14px 16px;
  margin-bottom: 10px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px 16px;
  align-items: start;
}
.risk-list li.elevated { border-left-color: var(--bad); }
.risk-list li.attention { border-left-color: var(--warn); }
.risk-list li.protective { border-left-color: var(--good); }
.risk-list li.neutral { border-left-color: var(--neutral); }
.risk-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}
.risk-name { font-weight: 600; font-size: 14px; }
.risk-body { font-size: 13px; color: var(--ink-mute); line-height: 1.5; }
.risk-cross {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--ink-mute);
  margin-top: 6px;
  letter-spacing: 0.04em;
}
.risk-side {
  text-align: right;
  font-family: var(--mono);
  font-size: 11px;
  color: var(--ink-mute);
  min-width: 100px;
}

/* VITALS GRID */
.vitals-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}
.vital {
  border: 1px solid var(--rule);
  padding: 10px 12px;
  background: var(--card);
}
.vital-label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.vital-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid var(--rule);
  padding: 4px 0;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 600;
  outline: none;
}
.vital-input:focus { border-bottom-color: var(--bad); }
.vital-unit {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-dim);
  margin-top: 2px;
}

/* REFERRAL */
.referral-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
.referral-card {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 14px 16px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
}
.referral-name { font-weight: 600; font-size: 14px; }
.referral-reason { font-size: 12px; color: var(--ink-mute); margin-top: 2px; }
.referral-action {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 10px;
  border: 1px solid var(--ink);
  cursor: pointer;
  background: white;
}
.referral-action.suggested {
  background: var(--bad);
  color: white;
  border-color: var(--bad);
}

/* PATIENT TALKING POINTS */
.talking-card {
  background: var(--warm);
  border: 1px solid var(--rule);
  padding: 18px 22px;
  margin-top: 14px;
}
.talking-eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 8px;
}
.talking-card p {
  margin: 0 0 8px 0;
  font-size: 14px;
  line-height: 1.6;
  font-style: italic;
  color: var(--ink-soft);
}
.talking-card p:last-child { margin-bottom: 0; }
.talking-card p::before { content: '"'; color: var(--bad); font-weight: 600; }
.talking-card p::after { content: '"'; color: var(--bad); font-weight: 600; }

/* FREE TEXT */
.notes-area {
  width: 100%;
  border: 1px solid var(--rule);
  padding: 12px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  min-height: 100px;
  background: var(--card);
  resize: vertical;
  outline: none;
}
.notes-area:focus { outline: 1px solid var(--bad); outline-offset: -1px; }

.sig-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-top: 24px;
}
.sig-line {
  border-top: 1px solid var(--ink);
  padding-top: 6px;
  font-size: 11px;
  color: var(--ink-mute);
  font-family: var(--mono);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.placeholder-note {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-dim);
  text-align: center;
  margin-top: 14px;
}

/* FOOTER */
.footer {
  background: var(--ink);
  color: white;
  padding: 32px;
  font-size: 12px;
}
.footer-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
}
.footer p {
  margin: 8px 0 0 0;
  color: var(--ink-dim);
  max-width: 600px;
}
.footer-meta {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--ink-dim);
  text-align: right;
}

/* PRINT */
@media print {
  .results6-wrap { background: white; }
  .topbar { background: white; color: black; }
  .hero, .card, .summary-tile, .pattern-card, .vital, .referral-card { background: white; }
  .section { padding: 20px; border: none; }
  .no-print { display: none; }
}
</style>
